/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useState
} from 'react';
import MaterialIconButton, {
  IconButtonProps as MUIIconButtonProps
} from '@material-ui/core/IconButton';
import {
  createTheme,
  ThemeProvider
} from '@material-ui/core/styles';
import omit from 'lodash.omit';
import {GenericObject} from '@ion/core/src/types/entities';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles, UseOverrideStylesProps} from '../../hooks/useExtendStyles';
import {Color} from '../../types/types';

export interface IconButtonProps extends Omit<MUIIconButtonProps, 'color'>, UseOverrideStylesProps {
  elementId?: string;
  icon: ReactElement;
  isSelected?: boolean;
  labelClass?: string;
  disableHover?: boolean;
  isDisableBackgroundColor?: boolean;
  backgroundColor?: null | string;
  color?: string | Color;
  shouldWrap?: boolean;
  padding?: string;
}

const iconButtonThemeBlack = (dlscolor) => {
  const primaryColor = dlscolor;
  return createTheme({
    overrides : {
      MuiSvgIcon : {
        colorPrimary : {
          color : primaryColor
        }
      }
    }
  });
};

const useStyles = (): GenericObject => {
  const background = ({disableHover}: IconButtonProps) =>
    !disableHover ? dlsAlt('components', 'btn', 'iconBackground') : 'inherit';
  return {
    root : {
      padding         : ({padding}: IconButtonProps) => padding || null,
      backgroundColor : ({backgroundColor}: IconButtonProps) => backgroundColor || 'none',
      boxShadow       : ({backgroundColor}: IconButtonProps) => {
        return backgroundColor ? ' 0px 4px 4px rgba(0, 0, 0, 0.25);' : 'none';
      },
      '&:hover' : {
        background
      },
      '&:active' : {
        background
      },
      '&:disabled' : {
        background : ({isDisableBackgroundColor}: IconButtonProps) => isDisableBackgroundColor ? dlsColor('white') : ''
      }
    },
    disabled      : {},
    rippleVisible : {
      opacity   : 0.01,
      animation : '$mui-ripple-enter 100ms cubic-bezier(0.4, 0, 0.2, 1)'
    },
    '@keyframes mui-ripple-enter' : {
      '0%' : {
        opacity : 0.01
      },
      '100%' : {
        opacity : 0.01
      }
    }
  };
};

interface WrappedIconButtonProps {
  shouldWrap?: boolean;
  onHoverProps: {
    onMouseEnter?: MouseEventHandler<HTMLElement>;
    onMouseLeave?: MouseEventHandler<HTMLElement>;
  }
}

/**
 * If the given {@link shouldWrap} flag is true, wraps the given children in a wrapping div.
 *
 * This solution was designed after issues with the onMouseLeave were only triggering on window leave, not button leave.
 */
const WrappedIconButton = ({
  children,
  onHoverProps,
  shouldWrap = false
}: PropsWithChildren<WrappedIconButtonProps>) => {
  return shouldWrap ? <div style={{display: 'inherit'}} {...onHoverProps}>{children}</div>
    : React.cloneElement(React.Children.only(children as ReactElement), {
      ref : undefined
    });
};

/**
 *
 * @IconButton
 *
 * Primary buttons are used to convey the most urgent or main actions on
 * a particular page. In cards, use the regular primary buttons. If paired,
 * use it with the tertiary button.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <IconButton icon={<SystemContactIcon size={IconSize.medium} />} />
 *       <br />
 *       <IconButton disabled icon={<SystemContactIcon size={IconSize.medium} />} />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 */
export const IconButton = forwardRef(({
  elementId,
  icon,
  isSelected = false,
  labelClass = null,
  disableHover = false,
  backgroundColor = null,
  shouldWrap = false,
  isDisableBackgroundColor = true,
  styleOverrides,
  styleOverridesProps,
  padding = null,
  ...props
}: IconButtonProps, ref) => {
  const classes: GenericObject = useExtendStyles(
    useStyles,
    styleOverrides
  )({backgroundColor, disableHover, isDisableBackgroundColor, padding, ...styleOverridesProps});

  const [isMouseDown, setIsMouseDown] = useState(false);
  const {color, disabled} = props;
  const childProps = omit(props, 'color');

  function getIconColor() {
    if (color) {
      return color;
    };
    if (disabled) {
      return dlsColor('grey4');
    }
    if (isMouseDown || isSelected) {
      return dlsColor('aaBlue');
    }
    return dlsColor('grey3');
  }

  const onMouseEnter = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsMouseDown(true);
    if (childProps.onMouseEnter) {
      childProps.onMouseEnter(event);
    }
  }, [childProps]);

  const onMouseLeave = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsMouseDown(false);
    if (childProps.onMouseLeave) {
      childProps.onMouseLeave(event);
    }
  }, [childProps]);

  return (
    <WrappedIconButton shouldWrap={shouldWrap}
      onHoverProps={{onMouseEnter, onMouseLeave}}>
      <MaterialIconButton disableRipple
        ref={ref}
        onMouseEnter={!shouldWrap ? onMouseEnter : undefined}
        onMouseLeave={!shouldWrap ? onMouseLeave : undefined}
        id={getValidElementId(elementId)}
        classes={{
          root     : classes.root,
          disabled : classes.disabled,
          label    : labelClass
        }}
        {...childProps}>
        <ThemeProvider theme={iconButtonThemeBlack(getIconColor())}>
          {React.cloneElement(icon, {
            color : getIconColor()
          })}
        </ThemeProvider>
      </MaterialIconButton>
    </WrappedIconButton>
  );
});
